import { effect, inject, Injectable } from '@angular/core';

import { BrowserWindowService } from '@spaces-ui/browser-window';
import { ElectronServiceBus } from '@spaces-ui/electron-service-bus';
import { ENVIRONMENT } from '@spaces-ui/environment';

@Injectable({
  providedIn: 'root',
})
export class LocationConfigService {
  private env = inject(ENVIRONMENT);
  private electronServiceBus = inject(ElectronServiceBus);
  private browserWindowService = inject(BrowserWindowService);

  public registrationRedirectBase?: string;
  public externalConnectionDiscoveryBase?: string;
  public magicLinkAuthRedirectBase?: string;
  public oauthAuthRedirectBase?: string;
  public ssoAuthRedirectBase?: string;
  public resetPasswordRedirectBase?: string;
  public browserLoginBase?: string;
  public browserExchangeSessionBase?: string;
  public appDownloadLocation = this.env.uri.appDownload;
  public electronProtocol = this.env.uri.electronProtocol;
  public prNumber?: string;
  public isLocalhost = false;
  public baseApiUrl = this.env.uri.apiBase;
  public stytchBaseApiUrl = this.env.uri.stytchApi;
  public assetsLocation?: string;
  public socketUrl = this.env.uri.webSocket;
  public appHome?: string;
  public callStandaloneBase?: string;
  public callRoomBase?: string;

  private appBase?: string;

  public constructor() {
    const isBlue = this.isBlue();
    this.prNumber = this.getPRNumber();
    this.isLocalhost = window.location.hostname === 'localhost';

    if (this.electronServiceBus.isElectronApp) {
      this.baseApiUrl = this.electronServiceBus.targetsBlueApi()
        ? this.env.uri.blueApiBase
        : this.env.uri.apiBase;
      this.browserWindowService.registerApiUrl(this.baseApiUrl);

      effect(() => {
        const targetsBlueApi = this.electronServiceBus.targetsBlueApi();

        this.baseApiUrl = targetsBlueApi ? this.env.uri.blueApiBase : this.env.uri.apiBase;
        this.socketUrl = targetsBlueApi ? this.env.uri.blueWebSocket : this.env.uri.webSocket;
        this.browserWindowService.registerApiUrl(this.baseApiUrl);
      });
    } else if (window.location.hostname === 'localhost') {
      this.baseApiUrl = this.env.uri.blueApiBase;
      this.socketUrl = this.env.uri.blueWebSocket;
      this.appDownloadLocation = this.env.uri.blueAppDownload;
      this.browserWindowService.registerApiUrl(this.baseApiUrl);
    } else {
      this.baseApiUrl = isBlue ? this.env.uri.blueApiBase : this.env.uri.apiBase;
      this.socketUrl = isBlue ? this.env.uri.blueWebSocket : this.env.uri.webSocket;
      this.appDownloadLocation = isBlue ? this.env.uri.blueAppDownload : this.env.uri.appDownload;
      this.browserWindowService.registerApiUrl(this.baseApiUrl);
    }

    this.appBase =
      window.location.hostname === 'localhost'
        ? `http://localhost:${window.location.port}`
        : `${this.baseApiUrl}s`;
    const domainBase = this.appBase.replace('/s', '');

    this.registrationRedirectBase = `${this.appBase}/registration-authenticate`;
    this.externalConnectionDiscoveryBase = `${this.appBase}/external-connection-authenticate`;
    this.magicLinkAuthRedirectBase = `${this.appBase}/magic-link-authenticate`;
    this.oauthAuthRedirectBase = `${this.appBase}/oauth-authenticate`;
    this.ssoAuthRedirectBase = `${this.appBase}/sso-authenticate`;
    this.resetPasswordRedirectBase = `${this.appBase}/reset-password-authenticate`;
    this.browserLoginBase = `${this.appBase}/login`;
    this.browserExchangeSessionBase = `${this.appBase}/electron-exchange-session`;
    this.assetsLocation = this.prNumber
      ? `${this.getPRLocation(this.prNumber)}/assets`
      : `${this.appBase}/assets`;
    this.appHome = this.prNumber ? this.getPRLocation(this.prNumber) : `${domainBase}`;
    this.callStandaloneBase = this.prNumber
      ? `${this.getPRLocation(this.prNumber)}/calls`
      : `${this.appBase}/calls`;
    // Browser join URL base for call rooms (`/call/<roomId>`). Singular on purpose: `/calls/<callId>`
    // is the popped-out call window, `/call/<roomId>` is the public landing page.
    this.callRoomBase = this.prNumber
      ? `${this.getPRLocation(this.prNumber)}/call`
      : `${this.appBase}/call`;
  }

  public getPRLocation(prNumber: string) {
    // takes the current app base (dev or blue dev) and gets the corresponding pr url
    const appBase = this.appBase ? this.appBase.replace('/s', '') : '';

    if (!appBase.length) {
      return '';
    }

    return `${appBase}/pr/${prNumber}/app`;
  }

  private isBlue() {
    const isBlue = window.location.hostname.indexOf('blue') > -1;

    return isBlue;
  }

  private getPRNumber() {
    const isPr = window.location.pathname.includes('/pr/');

    if (!isPr) {
      return undefined;
    }

    const prBase = window.location.pathname.split('/app/');
    const prNum = prBase[0].replace('/pr/', '');

    return prNum;
  }
}
